import { motion } from 'framer-motion';
import { Github, MessageSquare, FileText, GitBranch, Box, Database, Terminal, Plug } from 'lucide-react';

interface Integration {
  name: string;
  icon: React.ElementType;
  category: string;
  status: 'available' | 'coming';
}

const integrations: Integration[] = [
  { name: 'GitHub', icon: Github, category: 'Source Control', status: 'available' },
  { name: 'GitLab', icon: GitBranch, category: 'Source Control', status: 'available' },
  { name: 'Linear', icon: FileText, category: 'Project Management', status: 'available' },
  { name: 'Jira', icon: Box, category: 'Project Management', status: 'available' },
  { name: 'Slack', icon: MessageSquare, category: 'Communication', status: 'available' },
  { name: 'Discord', icon: MessageSquare, category: 'Communication', status: 'coming' },
  { name: 'PostgreSQL', icon: Database, category: 'Database', status: 'available' },
  { name: 'MongoDB', icon: Database, category: 'Database', status: 'coming' },
];

export function Ecosystem() {
  return (
    <section className="py-24 bg-surface-800">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left: Integration Grid */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-4 gap-4">
              {integrations.map((integration, index) => (
                <motion.div
                  key={integration.name}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={`
                    relative group p-4 bg-surface-900 rounded-xl border border-surface-600
                    hover:border-surface-500 transition-all flex flex-col items-center justify-center
                    ${integration.status === 'coming' ? 'opacity-60' : ''}
                  `}
                >
                  <integration.icon className="w-8 h-8 text-text-secondary group-hover:text-primary-400 transition-colors mb-2" />
                  <span className="text-xs text-text-tertiary text-center">{integration.name}</span>
                  {integration.status === 'coming' && (
                    <span className="absolute -top-2 -right-2 text-[10px] bg-surface-700 text-text-tertiary px-1.5 py-0.5 rounded-full">
                      Soon
                    </span>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: MCP Callout */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500/10 border border-accent-500/20 text-accent-400 text-sm font-medium mb-6">
              <Plug className="w-4 h-4" />
              Extensible by Design
            </div>

            <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
              Connect Everything via MCP
            </h2>

            <p className="text-lg text-text-secondary mb-8 leading-relaxed">
              Model Context Protocol enables seamless integration with your existing tools.
              Add custom data sources, connect internal APIs, extend agent capabilities.
            </p>

            <div className="bg-surface-900 rounded-xl border border-surface-600 p-6">
              <div className="flex items-center gap-3 mb-4">
                <Terminal className="w-5 h-5 text-accent-400" />
                <span className="font-mono text-sm text-text-secondary">mcp-server.config.ts</span>
              </div>

              <pre className="text-sm text-text-secondary overflow-x-auto">
                <code>{`{
  "servers": [
    { "name": "github", "type": "built-in" },
    { "name": "linear", "type": "built-in" },
    { "name": "internal-api",
      "type": "custom",
      "endpoint": "https://api.internal/mcp"
    }
  ]
}`}</code>
              </pre>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-900 rounded-lg text-sm text-text-secondary border border-surface-600">
                <span className="w-2 h-2 rounded-full bg-success-400" />
                8 built-in servers
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-900 rounded-lg text-sm text-text-secondary border border-surface-600">
                <span className="w-2 h-2 rounded-full bg-primary-400" />
                Custom server SDK
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-surface-900 rounded-lg text-sm text-text-secondary border border-surface-600">
                <span className="w-2 h-2 rounded-full bg-accent-400" />
                Community registry
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
